import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { gmsService } from '../services/gmsService'
import { DPSDrug } from '../types/gms'
import { formatDate } from '../utils/locale'
import './DPSDrugChecker.css'

interface DPSDrugCheckerProps {
  patientId: string
}

export function DPSDrugChecker({ patientId }: DPSDrugCheckerProps) {
  const [drugCodes, setDrugCodes] = useState<string[]>([''])
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<DPSDrug[]>([])

  const checkMutation = useMutation({
    mutationFn: (codes: string[]) => gmsService.checkDPSDrugs(patientId, codes.filter(Boolean)),
  })

  const searchMutation = useMutation({
    mutationFn: (term: string) => gmsService.searchDrug(term),
    onSuccess: (data) => {
      setSearchResults(data)
    },
  })

  const handleAddDrug = () => {
    setDrugCodes([...drugCodes, ''])
  }

  const handleRemoveDrug = (index: number) => {
    setDrugCodes(drugCodes.filter((_, i) => i !== index))
  }

  const handleDrugCodeChange = (index: number, value: string) => {
    const newCodes = [...drugCodes]
    newCodes[index] = value
    setDrugCodes(newCodes)
  }

  const handleSearch = async () => {
    if (searchTerm.trim()) {
      await searchMutation.mutateAsync(searchTerm)
    }
  }

  const handleSelectDrug = (drug: DPSDrug) => {
    setDrugCodes([...drugCodes.filter(Boolean), drug.drugCode])
    setSearchTerm('')
    setSearchResults([])
  }

  const handleCheckDrugs = async () => {
    const codes = drugCodes.filter(Boolean)
    if (codes.length > 0) {
      await checkMutation.mutateAsync(codes)
    }
  }

  return (
    <div className="dps-drug-checker" role="region" aria-label="DPS Drug Checker">
      <div className="card-header">
        <h3>Drug Payment Scheme (DPS) Checker</h3>
      </div>

      <div className="card-body">
        <div className="search-section">
          <h4>Search for Drugs</h4>
          <div className="search-input-group">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Enter drug name or code"
              className="search-input"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              aria-label="Search for drug"
            />
            <button
              className="btn-primary"
              onClick={handleSearch}
              disabled={searchMutation.isPending || !searchTerm.trim()}
            >
              {searchMutation.isPending ? 'Searching...' : 'Search'}
            </button>
          </div>

          {searchResults.length > 0 && (
            <div className="search-results">
              <h5>Search Results</h5>
              <ul className="drug-list">
                {searchResults.map((drug) => (
                  <li key={drug.drugCode} className="drug-item">
                    <div className="drug-info">
                      <span className="drug-name">{drug.drugName}</span>
                      <span className="drug-code">Code: {drug.drugCode}</span>
                      {drug.packSize !== undefined && (
                        <span className="drug-pack">Pack size: {drug.packSize}</span>
                      )}
                    </div>
                    <button
                      className="btn-secondary btn-small"
                      onClick={() => handleSelectDrug(drug)}
                      aria-label={`Add ${drug.drugName} to check list`}
                    >
                      Add
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="check-section">
          <h4>Check Drug Coverage</h4>
          <div className="drug-codes-input">
            {drugCodes.map((code, index) => (
              <div key={index} className="drug-code-input-group">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => handleDrugCodeChange(index, e.target.value)}
                  placeholder="Enter drug code"
                  className="drug-code-input"
                  aria-label={`Drug code ${index + 1}`}
                />
                {drugCodes.length > 1 && (
                  <button
                    className="btn-remove"
                    onClick={() => handleRemoveDrug(index)}
                    aria-label={`Remove drug code ${index + 1}`}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            <button
              className="btn-secondary btn-add-drug"
              onClick={handleAddDrug}
              aria-label="Add another drug code"
            >
              + Add Drug Code
            </button>
          </div>

          <button
            className="btn-primary btn-check"
            onClick={handleCheckDrugs}
            disabled={checkMutation.isPending || drugCodes.every((c) => !c.trim())}
            aria-label="Check drug coverage"
          >
            {checkMutation.isPending ? 'Checking...' : 'Check Coverage'}
          </button>
        </div>

        {checkMutation.data && (
          <div className="check-results">
            <h4>Check Results</h4>
            <div className="result-summary">
              <p>
                <strong>Checked:</strong> {formatDate(checkMutation.data.checkedAt)}
              </p>
              {checkMutation.data.totalPayment !== undefined && (
                <p>
                  <strong>Total Payment:</strong> €{checkMutation.data.totalPayment.toFixed(2)}
                </p>
              )}
            </div>
            <div className="drug-results">
              {checkMutation.data.drugs.map((drug) => (
                <div
                  key={drug.drugCode}
                  className={`drug-result ${drug.isCovered ? 'covered' : 'not-covered'}`}
                >
                  <div className="drug-result-header">
                    <span className="drug-name">{drug.drugName}</span>
                    <span className={`coverage-badge ${drug.isCovered ? 'covered' : 'not-covered'}`}>
                      {drug.isCovered ? '✓ Covered' : '✗ Not Covered'}
                    </span>
                  </div>
                  <div className="drug-result-details">
                    <span className="drug-code">Code: {drug.drugCode}</span>
                    {drug.maxPayment !== undefined && (
                      <span className="max-payment">Max Payment: €{drug.maxPayment.toFixed(2)}</span>
                    )}
                    {drug.notes && <span className="drug-notes">{drug.notes}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {checkMutation.isError && (
          <div className="error-message" role="alert">
            Failed to check drug coverage. Please try again.
          </div>
        )}
      </div>
    </div>
  )
}





















